import type { FieldPolicy, Reference } from "@apollo/client";
import { mergeDeep } from "@apollo/client/utilities";
import type {
  RelayFieldPolicy,
  TExistingRelay,
  TRelayEdge,
  TRelayPageInfo,
} from "@apollo/client/utilities/policies/pagination";
import { __rest } from "tslib";
import { blockUsersVar } from "~/components/blocks/vod/playback/store";

type KeyArgs = FieldPolicy<unknown>["keyArgs"];
const COMMENT_DISPLAY_LIMIT = 300;

// TODO:最適化
export function customRelayStylePagination<TNode extends Reference = Reference>(
  keyArgs: KeyArgs = false
): RelayFieldPolicy<TNode> {
  return {
    keyArgs,

    read(existing, { canRead, readField }) {
      if (!existing) return existing;

      const blockUsers = blockUsersVar();
      const filters = existing.edges.filter((edge) => {
        // @ts-ignore
        const hashedUserId = readField<string>("hashedUserId", edge.node);
        return !blockUsers.includes(hashedUserId as string);
      });

      const edges: TRelayEdge<TNode>[] = [];

      filters.forEach((edge) => {
        if (canRead(readField("node", edge))) {
          edges.push(edge);
        }
      });

      const visibleEdges = edges.slice(-COMMENT_DISPLAY_LIMIT);

      return {
        ...getExtras(existing),
        edges: visibleEdges,
        pageInfo: {
          ...existing.pageInfo,
          startCursor: visibleEdges[0]?.cursor,
          endCursor: existing.edges[existing.edges.length - 1]?.cursor,
        },
      };
    },

    merge(existing, incoming, { args, isReference, readField }) {
      if (!existing) {
        existing = makeEmptyData();
      }

      if (!incoming) {
        return existing;
      }

      const incomingEdges = incoming.edges
        ? incoming.edges.map((edge) => {
            if (isReference((edge = { ...edge }))) {
              edge.cursor = readField<string>("cursor", edge);
            }
            return edge;
          })
        : [];

      if (incoming.pageInfo) {
        const { pageInfo } = incoming;
        const { startCursor, endCursor } = pageInfo;
        const firstEdge = incomingEdges[0];
        const lastEdge = incomingEdges[incomingEdges.length - 1];

        if (firstEdge && startCursor) {
          firstEdge.cursor = startCursor;
        }
        if (lastEdge && endCursor) {
          lastEdge.cursor = endCursor;
        }

        const firstCursor = firstEdge && firstEdge.cursor;
        if (firstCursor && !startCursor) {
          incoming = mergeDeep(incoming, {
            pageInfo: {
              startCursor: firstCursor,
            },
          });
        }
        const lastCursor = lastEdge && lastEdge.cursor;
        if (lastCursor && !endCursor) {
          incoming = mergeDeep(incoming, {
            pageInfo: {
              endCursor: lastCursor,
            },
          });
        }
      }

      let prefix = existing.edges;

      let suffix: typeof prefix = [];
      const customArgsAfter = args && args.after?.toString();
      const customArgsBefore = args && args.before?.toString();
      if (customArgsAfter) {
        const index = prefix.findIndex((edge) => {
          return edge.cursor === customArgsAfter;
        });

        if (index >= 0) {
          prefix = prefix.slice(0, index + 1);
        }
      } else if (customArgsBefore) {
        const index = prefix.findIndex((edge) => edge.cursor === customArgsBefore);
        suffix = index < 0 ? prefix : prefix.slice(index);
        prefix = [];
      } else if (incoming.edges) {
        prefix = [];
      }

      const edges = [...prefix, ...incomingEdges, ...suffix];

      const pageInfo: TRelayPageInfo = {
        ...incoming.pageInfo,
        ...existing.pageInfo,
      };

      if (incoming.pageInfo) {
        const { hasPreviousPage, hasNextPage, startCursor, endCursor, ...extras } = incoming.pageInfo;
        Object.assign(pageInfo, extras);
        if (!prefix.length) {
          if (void 0 !== hasPreviousPage) pageInfo.hasPreviousPage = hasPreviousPage;
          if (void 0 !== startCursor) pageInfo.startCursor = startCursor;
        }
        if (!suffix.length) {
          if (void 0 !== hasNextPage) pageInfo.hasNextPage = hasNextPage;
          if (void 0 !== endCursor) pageInfo.endCursor = endCursor;
        }
      }

      return {
        ...getExtras(existing),
        ...getExtras(incoming),
        edges,
        pageInfo,
      };
    },
  };
}

const getExtras = (obj: Record<string, unknown>) => __rest(obj, notExtras);
const notExtras = ["edges", "pageInfo"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeEmptyData(): TExistingRelay<any> {
  return {
    edges: [],
    pageInfo: {
      hasPreviousPage: false,
      hasNextPage: true,
      startCursor: "",
      endCursor: "",
    },
  };
}
